import * as z$1 from "zod/v4";
//#region src/schemas/http/cli-binary-manifest.ts
const SHA256_HEX_PATTERN = /^[a-f0-9]{64}$/u;
function isBase64(value) {
	if (value.length === 0 || value.length % 4 !== 0) return false;
	let contentEnd = value.length;
	if (value.endsWith("==")) contentEnd -= 2;
	else if (value.endsWith("=")) contentEnd -= 1;
	for (let index = 0; index < contentEnd; index += 1) {
		const code = value.charCodeAt(index);
		if (!(code >= 65 && code <= 90 || code >= 97 && code <= 122 || code >= 48 && code <= 57 || code === 43 || code === 47)) return false;
	}
	return true;
}
const cliBinaryArtifactSchema = z$1.object({
	sha256: z$1.string().regex(SHA256_HEX_PATTERN),
	sizeBytes: z$1.number().int().positive(),
	url: z$1.url()
});
const cliBinaryArchiveTransportSchema = z$1.object({
	archiveFormat: z$1.literal("gzip"),
	archiveSha256: z$1.string().regex(SHA256_HEX_PATTERN),
	archiveSizeBytes: z$1.number().int().positive(),
	archiveUrl: z$1.url()
});
const cliBinaryCompressedReleaseSchema = cliBinaryArtifactSchema.extend(cliBinaryArchiveTransportSchema.shape);
const cliBinaryDeltaPatchSchema = z$1.object({
	baseSha256: z$1.string().regex(SHA256_HEX_PATTERN),
	baseSizeBytes: z$1.number().int().positive(),
	blockSizeBytes: z$1.literal(4096),
	patchFormat: z$1.literal("block-ranges-v1"),
	schemaVersion: z$1.literal(1),
	segments: z$1.array(z$1.object({
		dataBase64: z$1.string().min(4).refine(isBase64, "invalid base64"),
		offset: z$1.number().int().nonnegative()
	})).max(1e5),
	targetSha256: z$1.string().regex(SHA256_HEX_PATTERN),
	targetSizeBytes: z$1.number().int().positive()
});
const cliBinaryDeltaTransportSchema = z$1.object({
	fromSha256: z$1.string().regex(SHA256_HEX_PATTERN),
	fromSizeBytes: z$1.number().int().positive(),
	fromVersion: z$1.string().trim().min(1),
	patchCompression: z$1.literal("gzip"),
	patchFormat: z$1.literal("block-ranges-v1"),
	patchPayloadSizeBytes: z$1.number().int().positive(),
	patchSha256: z$1.string().regex(SHA256_HEX_PATTERN),
	patchSizeBytes: z$1.number().int().positive(),
	patchUrl: z$1.url()
});
const cliBinaryReleaseSchema = cliBinaryArtifactSchema.extend({
	archiveFormat: cliBinaryArchiveTransportSchema.shape.archiveFormat.optional(),
	archiveSha256: cliBinaryArchiveTransportSchema.shape.archiveSha256.optional(),
	archiveSizeBytes: cliBinaryArchiveTransportSchema.shape.archiveSizeBytes.optional(),
	archiveUrl: cliBinaryArchiveTransportSchema.shape.archiveUrl.optional(),
	codeIdentifier: z$1.string().trim().min(1).optional(),
	deltas: z$1.array(cliBinaryDeltaTransportSchema).max(8).optional(),
	teamIdentifier: z$1.string().trim().min(1).optional()
}).superRefine((release, context) => {
	const archiveFields = [
		release.archiveFormat,
		release.archiveSha256,
		release.archiveSizeBytes,
		release.archiveUrl
	];
	if (!(archiveFields.every((value) => value === void 0) || archiveFields.every((value) => value !== void 0))) context.addIssue({
		code: "custom",
		message: "compressed transport fields must be provided together"
	});
	if (release.codeIdentifier === void 0 !== (release.teamIdentifier === void 0)) context.addIssue({
		code: "custom",
		message: "signature identity fields must be provided together"
	});
	const deltaBases = /* @__PURE__ */ new Set();
	for (const delta of release.deltas ?? []) {
		const baseKey = `${delta.fromVersion}:${delta.fromSha256}`;
		if (deltaBases.has(baseKey)) {
			context.addIssue({
				code: "custom",
				message: "delta base records must be unique"
			});
			break;
		}
		deltaBases.add(baseKey);
	}
});
const binaryPlatformsSchema = z$1.record(z$1.string().trim().min(1), cliBinaryReleaseSchema).refine((platforms) => Object.keys(platforms).length > 0, { message: "at least one platform is required" });
const cliBinaryServiceReleaseSchema = z$1.object({
	platforms: binaryPlatformsSchema,
	version: z$1.string().trim().min(1)
});
const cliBinaryManifestSchema = z$1.object({
	channels: z$1.object({ stable: z$1.object({
		minRequiredCliVersion: z$1.string().trim().min(1).nullable().optional(),
		platforms: binaryPlatformsSchema,
		service: cliBinaryServiceReleaseSchema.optional(),
		version: z$1.string().trim().min(1)
	}).superRefine((stable, context) => {
		if (!stable.service) return;
		for (const platform of Object.keys(stable.platforms)) if (!stable.service.platforms[platform]) context.addIssue({
			code: "custom",
			path: [
				"service",
				"platforms",
				platform
			],
			message: "a Service artifact is required for every CLI platform"
		});
	}) }),
	schemaVersion: z$1.literal(1)
});
//#endregion
export { cliBinaryCompressedReleaseSchema, cliBinaryDeltaPatchSchema, cliBinaryDeltaTransportSchema, cliBinaryManifestSchema, cliBinaryReleaseSchema, cliBinaryServiceReleaseSchema };

//# sourceMappingURL=cli-binary-manifest.js.map